import { useState } from "react";
import { Globe, Plus, Search, Loader2, TrendingUp, Users, RefreshCw, Settings2, UploadCloud } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompany } from "@/hooks/useCompany";
import { useAuth } from "@/hooks/useAuth";
import { useCompanyPackageAccess } from "@/hooks/useCompanyPackageAccess";
import { useCompanyUsage } from "@/hooks/useCompanyUsage";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { formatDistanceToNow } from "date-fns";
import { enUS, fr as frLocale } from "date-fns/locale";
import SiteActionCenter from "@/components/websites/SiteActionCenter";
import SiteManagementPanel from "@/components/websites/SiteManagementPanel";
import { Check } from "lucide-react";
import { toast } from "sonner";

const websitesDb = supabase as any;

const trialDemoWebsites = [
  {
    id: "trial-demo-citywire",
    owner_id: "trial-demo-owner",
    name: "Demo City Journal",
    domain: "demo-city.delapresse.site",
    slug: "demo-city-journal",
    status: "active",
    article_count: 18,
    languages: ["en", "fr"],
    niche: "Local news",
    updated_at: new Date(Date.now() - 1000 * 60 * 38).toISOString(),
    provisioning_status: "live",
    bandwidth_mb: 860,
    logo_url: null,
    deployment_strategy: "auto",
    company_id: "trial-demo-company",
    external_hosting_provider: null,
    sync_enabled: false,
    publish_status: "demo",
    sync_status: "demo",
    last_publish_at: new Date(Date.now() - 1000 * 60 * 65).toISOString(),
    last_sync_at: new Date(Date.now() - 1000 * 60 * 44).toISOString(),
    last_publish_error: null,
    last_sync_error: null,
    is_trial_demo: true,
  },
  {
    id: "trial-demo-business",
    owner_id: "trial-demo-owner",
    name: "Demo Business Desk",
    domain: "business-demo.delapresse.site",
    slug: "demo-business-desk",
    status: "active",
    article_count: 9,
    languages: ["en"],
    niche: "Business",
    updated_at: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    provisioning_status: "pending",
    bandwidth_mb: 240,
    logo_url: null,
    deployment_strategy: "manual",
    company_id: "trial-demo-company",
    external_hosting_provider: null,
    sync_enabled: false,
    publish_status: "demo",
    sync_status: "demo",
    last_publish_at: null,
    last_sync_at: null,
    last_publish_error: null,
    last_sync_error: null,
    is_trial_demo: true,
  },
];

async function fetchWebsites(companyId: string | null, isPlatformOwner: boolean, userId: string | undefined, role: string | null) {
  let query = websitesDb
    .from("websites")
    .select("id, owner_id, name, domain, slug, status, article_count, languages, niche, updated_at, provisioning_status, bandwidth_mb, logo_url, deployment_strategy, company_id, external_hosting_provider, sync_enabled, publish_status, sync_status, last_publish_at, last_sync_at, last_publish_error, last_sync_error")
    .order("created_at", { ascending: false });
  // Platform owner sees all rows; everyone else is scoped to their company.
  // Defence-in-depth on top of RLS — never trust RLS alone (CLAUDE.md
  // multi-tenancy rule).
  if (companyId) {
    query = query.eq("company_id", companyId);
  }
  if (!isPlatformOwner && !["owner", "admin"].includes(role ?? "") && userId) {
    query = query.eq("owner_id", userId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function fetchOwners(ownerIds: string[]) {
  if (ownerIds.length === 0) return {};
  const { data, error } = await websitesDb
    .from("profiles")
    .select("id, full_name")
    .in("id", ownerIds);
  if (error) throw error;
  return Object.fromEntries((data || []).map((profile: any) => [profile.id, profile.full_name || "Unknown"]));
}

async function fetchCompanies(companyIds: string[]) {
  if (companyIds.length === 0) return {};
  const { data, error } = await websitesDb
    .from("companies")
    .select("id, name")
    .in("id", companyIds);
  if (error) throw error;
  return Object.fromEntries((data || []).map((company: any) => [company.id, company.name || "Unknown"]));
}

async function fetchHostingConnections(websiteIds: string[]) {
  if (websiteIds.length === 0) return [];
  let query = websitesDb
    .from("website_hosting_connection_summaries")
    .select("id, website_id, provider, validation_status, last_validated_at, last_validation_error, public_config, updated_at")
    .in("website_id", websiteIds)
    .order("updated_at", { ascending: false });
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export default function WebsitesPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("fr") ? frLocale : enUS;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentCompanyId, isPlatformOwner, role, isLoading: companyLoading } = useCompany();
  const { package: packageInfo, canCreateWebsites, websiteLimit } = useCompanyPackageAccess();
  const { usage } = useCompanyUsage();
  const trialMode = useTrialMode();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<{ id: string; name: string; domain: string | null } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const hasDeploying = (sites: any[]) => sites.some((s) => s.provisioning_status === "deploying");

  const { data: websites = [], isLoading, refetch } = useQuery({
    queryKey: ["websites", currentCompanyId, isPlatformOwner, role, user?.id],
    queryFn: () => fetchWebsites(currentCompanyId, isPlatformOwner, user?.id, role),
    enabled: !companyLoading && (isPlatformOwner || !!currentCompanyId),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && hasDeploying(data) ? 10_000 : false;
    },
  });

  const { data: hostingConnections = [] } = useQuery({
    queryKey: ["website-hosting-summaries", websites.map((site: any) => site.id).join(",")],
    queryFn: () => fetchHostingConnections(websites.map((site: any) => site.id)),
    enabled: websites.length > 0,
  });

  const ownerIds = Array.from(new Set(websites.map((site: any) => site.owner_id).filter(Boolean))) as string[];
  const { data: ownersMap = {} } = useQuery({
    queryKey: ["website-owners", ownerIds.join(",")],
    queryFn: () => fetchOwners(ownerIds),
    enabled: ownerIds.length > 0,
  });

  const companyIds = Array.from(new Set(websites.map((site: any) => site.company_id).filter(Boolean))) as string[];
  const { data: companiesMap = {} } = useQuery({
    queryKey: ["website-companies", companyIds.join(",")],
    queryFn: () => fetchCompanies(companyIds),
    enabled: companyIds.length > 0,
  });

  const hostingByWebsite: Record<string, any> = (hostingConnections as any[]).reduce((acc: Record<string, any>, connection: any) => {
    if (!acc[connection.website_id]) acc[connection.website_id] = connection;
    return acc;
  }, {});

  const displayWebsites = trialMode.isReadOnly && websites.length === 0 ? trialDemoWebsites : websites;
  const filtered = displayWebsites.filter((s) => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.domain?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && s.provisioning_status !== statusFilter) return false;
    return true;
  });

  // Live status indicator
  const getLiveStatus = (site: any) => {
    if (site.provisioning_status === "error") return { color: "bg-destructive", label: "Critical" };
    if (site.provisioning_status === "deploying") return { color: "bg-warning animate-pulse", label: "Syncing" };
    if (site.provisioning_status === "dns_issues") return { color: "bg-warning", label: "DNS Issue" };
    if (site.provisioning_status === "live") return { color: "bg-success", label: "Healthy" };
    return { color: "bg-muted-foreground", label: "Offline" };
  };

  // Bulk sync all
  const handleBulkSync = async () => {
    if (trialMode.isReadOnly) {
      toast.error(TRIAL_READ_ONLY_MESSAGE);
      return;
    }
    const targets = selectedIds.size > 0
      ? displayWebsites.filter((s) => selectedIds.has(s.id) && hostingByWebsite[s.id])
      : displayWebsites.filter((s) => hostingByWebsite[s.id]);

    if (targets.length === 0) {
      toast.error("No websites with external hosting are selected");
      return;
    }

    setBulkSyncing(true);
    let ok = 0;
    for (const site of targets) {
      try {
        await supabase.functions.invoke("website-hosting-manager", {
          body: { action: "sync", website_id: site.id, connection_id: hostingByWebsite[site.id].id },
        });
        ok++;
      } catch { /* continue */ }
    }
    setBulkSyncing(false);
    toast.success(`Sync initiated for ${ok} site(s)`);
    refetch();
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.filter((s: any) => !s.is_trial_demo).map((s) => s.id)));
    }
  };

  // Summary stats
  const liveSites = displayWebsites.filter((s) => s.provisioning_status === "live").length;
  const totalArticles = (displayWebsites as any[]).reduce((sum: number, s: any) => sum + (s.article_count || 0), 0);
  const totalBandwidthGb = ((displayWebsites as any[]).reduce((sum: number, s: any) => sum + (s.bandwidth_mb || 0), 0) / 1024).toFixed(1);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t("websites.title")}</h1>
          <p className="text-xs text-muted-foreground">
            {t("websites.subtitle", { total: displayWebsites.length, live: liveSites, articles: totalArticles.toLocaleString() })}
            {trialMode.isReadOnly && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Demo data</span>}
            {websiteLimit && (
              <span className="ml-3 inline-block px-2 py-1 bg-muted rounded text-xs">
                {websites.length}/{websiteLimit}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            if (websiteLimit && usage.websites_created >= websiteLimit) {
              toast.error(`You've reached your website limit of ${websiteLimit}`);
              return;
            }
            if (!canCreateWebsites) {
              toast.error(trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : "Website creation is not available in your plan");
              return;
            }
            navigate("/websites/create");
          }}
          disabled={!canCreateWebsites || (websiteLimit && usage.websites_created >= websiteLimit)}
          className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={websiteLimit ? `Websites: ${usage.websites_created}/${websiteLimit}` : undefined}
        >
          <Plus className="h-4 w-4" />
          {t("websites.newWebsite")}
        </button>
      </header>

      <div className="p-6 animate-slide-in">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SummaryCard label={t("websites.summary.total")} value={displayWebsites.length} icon={Globe} />
          <SummaryCard label={t("websites.summary.live")} value={liveSites} icon={Check} accent />
          <SummaryCard label={t("websites.summary.articles")} value={totalArticles} icon={TrendingUp} />
          <SummaryCard label={t("websites.summary.bandwidth")} value={`${totalBandwidthGb} GB`} icon={Users} />
        </div>

        {/* Filters + Bulk Actions */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 flex-1 max-w-sm">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1"
                placeholder={t("websites.filters.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1">
              {(["live", "deploying", "dns_issues", "pending"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide transition-colors border",
                    statusFilter === s
                      ? "bg-primary/10 border-primary/30 text-primary"
                      : "bg-card border-border text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t(`websites.filters.${s}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk action bar */}
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <span className="text-[10px] text-muted-foreground">{t("websites.bulk.selected", { count: selectedIds.size })}</span>
            )}
            <button
              onClick={handleBulkSync}
              disabled={bulkSyncing || trialMode.isReadOnly}
              title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            >
              <RefreshCw className={cn("h-3 w-3", bulkSyncing && "animate-spin")} />
              {selectedIds.size > 0 ? t("websites.bulk.syncSelected") : t("websites.bulk.syncAll")}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-3 w-8">
                    <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} className="rounded border-border" />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("websites.table.monitor")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("websites.table.website")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("websites.table.owner")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Hosting</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Publish / Sync</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("websites.table.updated")}</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {isLoading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    {displayWebsites.length === 0 ? t("websites.empty") : t("websites.noMatch")}
                  </td></tr>
                ) : filtered.map((site) => {
                  const hosting = hostingByWebsite[site.id];
                  const liveStatus = getLiveStatus(site);

                  return (
                    <tr
                      key={site.id}
                      className="hover:bg-accent/30 transition-colors cursor-pointer"
                      onClick={() => {
                        if ((site as any).is_trial_demo) {
                          toast.error(TRIAL_READ_ONLY_MESSAGE);
                          return;
                        }
                        setSelectedSite({ id: site.id, name: site.name, domain: site.domain });
                      }}
                    >
                      {/* Checkbox */}
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(site.id)}
                          onChange={() => {}}
                          onClick={(e) => toggleSelect(site.id, e)}
                          disabled={(site as any).is_trial_demo}
                          className="rounded border-border"
                        />
                      </td>

                      {/* Live status dot */}
                      <td className="px-3 py-3">
                        <div className="relative group">
                          <div className={cn("h-2.5 w-2.5 rounded-full", liveStatus.color)} />
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 hidden group-hover:block bg-popover text-popover-foreground border border-border rounded px-1.5 py-0.5 text-[9px] whitespace-nowrap z-10 shadow-sm">
                            {liveStatus.label}
                          </span>
                        </div>
                      </td>

                      {/* Website name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {site.logo_url ? (
                            <img src={site.logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-foreground">{site.name}</p>
                              {(site as any).is_trial_demo && (
                                <span className="rounded bg-amber-100 px-1 py-0.5 text-[8px] font-bold uppercase text-amber-800">Demo</span>
                              )}
                              {site.deployment_strategy === "auto" && (
                                <span className="rounded bg-primary/10 px-1 py-0.5 text-[8px] font-bold text-primary uppercase">{t("websites.autoBadge")}</span>
                              )}
                              <HostingBadge configured={Boolean(hosting)} provider={hosting?.provider} />
                            </div>
                            <p className="text-xs text-muted-foreground">{site.domain || site.slug}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground">{(site as any).is_trial_demo ? "Demo editor" : ownersMap[site.owner_id] || t("websites.unknownOwner")}</span>
                      </td>

                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground">{(site as any).is_trial_demo ? "Demo workspace" : companiesMap[site.company_id] || "Unknown"}</span>
                      </td>

                      {/* Hosting */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          {hosting ? (
                            <>
                              <StatusBadge status={hosting.validation_status} />
                              <span className="text-[10px] text-muted-foreground uppercase">{String(hosting.provider).replace("_", "/")}</span>
                            </>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">Delapresse-managed</span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <button
                            onClick={() => (site as any).is_trial_demo ? toast.error(TRIAL_READ_ONLY_MESSAGE) : navigate(`/websites/${site.id}`)}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-medium text-foreground hover:bg-accent"
                          >
                            <Settings2 className="h-3 w-3" />
                            Manage
                          </button>
                          <button
                            disabled={!hosting || bulkSyncing || trialMode.isReadOnly}
                            title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}
                            onClick={async () => {
                              const { error } = await supabase.functions.invoke("website-hosting-manager", { body: { action: "publish", website_id: site.id, connection_id: hosting.id } });
                              if (error) toast.error(error.message);
                              else toast.success("Publish requested");
                              queryClient.invalidateQueries({ queryKey: ["websites"] });
                              queryClient.invalidateQueries({ queryKey: ["website-hosting-summaries"] });
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-medium text-foreground hover:bg-accent disabled:opacity-40"
                          >
                            <UploadCloud className="h-3 w-3" />
                            Publish
                          </button>
                          <button
                            disabled={!hosting || bulkSyncing || trialMode.isReadOnly}
                            title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}
                            onClick={async () => {
                              const { error } = await supabase.functions.invoke("website-hosting-manager", { body: { action: "sync", website_id: site.id, connection_id: hosting.id } });
                              if (error) toast.error(error.message);
                              else toast.success("Sync requested");
                              queryClient.invalidateQueries({ queryKey: ["websites"] });
                              queryClient.invalidateQueries({ queryKey: ["website-hosting-summaries"] });
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-medium text-foreground hover:bg-accent disabled:opacity-40"
                          >
                            <RefreshCw className="h-3 w-3" />
                            Sync
                          </button>
                        </div>
                        <div className="mt-1 flex flex-col gap-0.5 text-[10px] text-muted-foreground">
                          <span>Publish: {site.publish_status || "not_published"}{site.last_publish_at ? ` · ${formatDistanceToNow(new Date(site.last_publish_at), { addSuffix: true, locale: dateLocale })}` : ""}</span>
                          <span>Sync: {site.sync_status || "not_synced"}{site.last_sync_at ? ` · ${formatDistanceToNow(new Date(site.last_sync_at), { addSuffix: true, locale: dateLocale })}` : ""}</span>
                          {(site.last_publish_error || site.last_sync_error) && <span className="text-destructive">{site.last_publish_error || site.last_sync_error}</span>}
                        </div>
                      </td>

                      {/* Updated */}
                      <td className="px-4 py-3 text-xs text-muted-foreground">{formatDistanceToNow(new Date(site.updated_at), { addSuffix: true, locale: dateLocale })}</td>

                      {/* Actions */}
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {(site as any).is_trial_demo ? (
                          <span className="text-[10px] text-muted-foreground">Read-only</span>
                        ) : (
                          <SiteActionCenter siteId={site.id} domain={site.domain} onDeployStarted={() => refetch()} onDeleted={() => refetch()} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Full-screen Site Panel */}
      {selectedSite && (
        <SiteManagementPanel site={selectedSite} onClose={() => setSelectedSite(null)} />
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, accent }: { label: string; value: string | number; icon: React.ElementType; accent?: boolean }) {
  return (
    <div className={cn("rounded-lg border p-4 flex items-center gap-3", accent ? "border-success/30 bg-success/5" : "border-border bg-card")}>
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", accent ? "bg-success/15" : "bg-accent")}>
        <Icon className={cn("h-4 w-4", accent ? "text-success" : "text-muted-foreground")} />
      </div>
      <div>
        <p className="text-xl font-bold text-foreground">{typeof value === "number" ? value.toLocaleString() : value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

function HostingBadge({ configured, provider }: { configured: boolean; provider?: string | null }) {
  const label = configured ? `External ${String(provider || "").replace("_", "/")}` : "Delapresse";
  return (
    <span className="rounded bg-accent px-1 py-0.5 text-[8px] font-bold uppercase text-muted-foreground">
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  const connected = status === "connected";
  const failed = status === "failed";
  return (
    <span className={cn(
      "rounded-full px-2 py-0.5 text-[10px] font-medium capitalize",
      connected && "bg-emerald-500/10 text-emerald-500",
      failed && "bg-destructive/10 text-destructive",
      !connected && !failed && "bg-muted text-muted-foreground",
    )}>
      {(status || "not configured").replace(/_/g, " ")}
    </span>
  );
}
